import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { ChevronRight, History, Send, CalendarPlus } from 'lucide-react';
import { toast } from 'sonner';
import { madridTime, madridLocalInput } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDay = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' }); }
  catch { return iso; }
};
const fmtTime = (iso) => {
  if (!iso) return '—';
  return madridTime(iso, '—');
};
const toLocalInput = (iso) => {
  return madridLocalInput(iso);
};

const todayIso = () => {
  const d = new Date();
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * EmployeeCorrectionsDialog — lets the worker review their last 30 records and
 * either propose a correction on an existing row OR declare a fully missed day
 * (when they forgot to clock in at all). All correction requests are reviewed
 * by the admin from CorrectionRequestsPanel.
 */
const EmployeeCorrectionsDialog = ({ open, onClose, employeeId, pin }) => {
  const [records, setRecords] = useState([]);
  const [requests, setRequests] = useState([]);
  const [editing, setEditing] = useState(null); // record being edited
  const [form, setForm] = useState({ clock_in: '', clock_out: '', reason: '' });
  // Missed-day flow (no underlying record yet)
  const [missedOpen, setMissedOpen] = useState(false);
  const [missedForm, setMissedForm] = useState({ date: todayIso(), clock_in: '', clock_out: '', reason: '' });
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!employeeId || !pin) return;
    setLoading(true);
    try {
      const [recRes, reqRes] = await Promise.all([
        fetch(`${API_URL}/api/timeclock/public/records/mine?employee_pin=${encodeURIComponent(pin)}&limit=30`),
        fetch(`${API_URL}/api/timeclock/public/correction-requests/mine?employee_pin=${encodeURIComponent(pin)}`),
      ]);
      if (recRes.ok) setRecords(await recRes.json());
      if (reqRes.ok) setRequests(await reqRes.json());
    } catch (e) {
      toast.error('No se pudieron cargar los registros');
    } finally { setLoading(false); }
  }, [employeeId, pin]);

  useEffect(() => { if (open) fetchAll(); }, [open, fetchAll]);

  const startEdit = (rec) => {
    setMissedOpen(false);
    setEditing(rec);
    setForm({
      clock_in: toLocalInput(rec.clock_in),
      clock_out: toLocalInput(rec.clock_out),
      reason: '',
    });
  };
  const cancelEdit = () => { setEditing(null); setForm({ clock_in: '', clock_out: '', reason: '' }); };

  const submit = async () => {
    if (!form.reason || form.reason.trim().length < 4) {
      toast.error('Indica un motivo (mínimo 4 caracteres)'); return;
    }
    if (!form.clock_in && !form.clock_out) {
      toast.error('Propón al menos una hora a corregir'); return;
    }
    try {
      const body = {
        record_id: editing.id,
        employee_pin: pin,
        proposed_clock_in: form.clock_in ? new Date(form.clock_in).toISOString() : null,
        proposed_clock_out: form.clock_out ? new Date(form.clock_out).toISOString() : null,
        reason: form.reason.trim(),
      };
      const res = await fetch(`${API_URL}/api/timeclock/public/correction-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Solicitud enviada al admin');
      cancelEdit();
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar');
    }
  };

  const submitMissed = async () => {
    const m = missedForm;
    if (!m.date) { toast.error('Indica la fecha'); return; }
    if (!m.clock_in) { toast.error('Indica la hora de entrada'); return; }
    if (!m.reason || m.reason.trim().length < 4) { toast.error('Indica el motivo'); return; }
    try {
      // Compose local datetimes for the picked date and times so timezone
      // matches the worker's perception of "ese día a las 11:00".
      const clockIn = new Date(`${m.date}T${m.clock_in}:00`).toISOString();
      const clockOut = m.clock_out ? new Date(`${m.date}T${m.clock_out}:00`).toISOString() : null;
      const body = {
        employee_pin: pin,
        date: m.date,
        proposed_clock_in: clockIn,
        proposed_clock_out: clockOut,
        reason: m.reason.trim(),
      };
      const res = await fetch(`${API_URL}/api/timeclock/public/correction-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Error');
      toast.success('Solicitud de día olvidado enviada al admin');
      setMissedOpen(false);
      setMissedForm({ date: todayIso(), clock_in: '', clock_out: '', reason: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar');
    }
  };

  const requestForRecord = (recId) => requests.find(r => r.record_id === recId);
  const missedDayRequests = requests.filter(r => !r.record_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="employee-corrections-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History size={20} /> Mis registros
          </DialogTitle>
          <DialogDescription>
            Si detectas un error, propón la corrección y el admin la revisará.
          </DialogDescription>
        </DialogHeader>

        {/* ── Missed day CTA + form ──────────────────────────────────────── */}
        <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="missed-day-block">
          {!missedOpen ? (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-amber-900">
                <CalendarPlus size={18} />
                <span>¿Olvidaste fichar un día entero?</span>
              </div>
              <Button size="sm" onClick={() => { setMissedOpen(true); setEditing(null); }} data-testid="missed-day-open-btn">
                Solicitar día olvidado
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm font-semibold text-amber-900">Día olvidado — propón el horario real</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-stone-600 block mb-1">Fecha</label>
                  <Input type="date" value={missedForm.date} max={todayIso()}
                    onChange={(e) => setMissedForm(f => ({ ...f, date: e.target.value }))}
                    data-testid="missed-form-date" className="text-sm" />
                </div>
                <div>
                  <label className="text-xs text-stone-600 block mb-1">Entrada</label>
                  <Input type="time" value={missedForm.clock_in}
                    onChange={(e) => setMissedForm(f => ({ ...f, clock_in: e.target.value }))}
                    data-testid="missed-form-clock-in" className="text-sm" />
                </div>
                <div>
                  <label className="text-xs text-stone-600 block mb-1">Salida (opcional)</label>
                  <Input type="time" value={missedForm.clock_out}
                    onChange={(e) => setMissedForm(f => ({ ...f, clock_out: e.target.value }))}
                    data-testid="missed-form-clock-out" className="text-sm" />
                </div>
              </div>
              <Textarea value={missedForm.reason}
                onChange={(e) => setMissedForm(f => ({ ...f, reason: e.target.value }))}
                placeholder="Ej: me olvidé de fichar entero el sábado pasado"
                rows={2} data-testid="missed-form-reason" className="text-sm" />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitMissed} data-testid="missed-form-submit">
                  <Send className="w-3 h-3 mr-1" /> Enviar solicitud
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMissedOpen(false)}>Cancelar</Button>
              </div>
            </div>
          )}

          {/* List pending missed-day requests for the worker's awareness */}
          {missedDayRequests.length > 0 && (
            <div className="mt-3 pt-3 border-t border-amber-200 space-y-1">
              <p className="text-xs font-semibold text-amber-900">Mis solicitudes de día olvidado:</p>
              {missedDayRequests.map(r => (
                <div key={r.id} className="flex items-center justify-between text-xs text-amber-800" data-testid={`missed-req-${r.id}`}>
                  <span>{r.date} · {madridTime(r.proposed_clock_in, '')}{r.proposed_clock_out ? ` → ${madridTime(r.proposed_clock_out, '')}` : ''}</span>
                  <span className={`px-2 py-0.5 rounded ${
                    r.status === 'pending' ? 'bg-amber-200 text-amber-900' :
                    r.status === 'approved' ? 'bg-green-200 text-green-900' :
                    'bg-red-200 text-red-900'
                  }`}>
                    {r.status === 'pending' ? 'Pendiente' : r.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        {loading && <p className="text-sm text-stone-500 py-4">Cargando…</p>}

        {!loading && records.length === 0 && (
          <p className="text-sm text-stone-500 py-4">Aún no tienes registros.</p>
        )}

        <div className="space-y-2">
          {records.map(rec => {
            const existingReq = requestForRecord(rec.id);
            const isEditing = editing?.id === rec.id;
            const closed = rec.is_closed;
            return (
              <div key={rec.id} className="border rounded-lg p-3 bg-white" data-testid={`emp-rec-${rec.id}`}>
                <div className="flex justify-between items-center text-sm">
                  <div>
                    <p className="font-semibold">{fmtDay(rec.clock_in)}</p>
                    <p className="text-stone-600">
                      {fmtTime(rec.clock_in)} → {fmtTime(rec.clock_out)}
                      {rec.total_hours ? ` · ${rec.total_hours}h` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {closed && <span className="text-[11px] text-stone-500 bg-stone-100 px-2 py-0.5 rounded">Cerrado</span>}
                    {existingReq && (
                      <span className={`text-[11px] px-2 py-0.5 rounded ${
                        existingReq.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                        existingReq.status === 'approved' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {existingReq.status === 'pending' ? 'Pendiente' : existingReq.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                      </span>
                    )}
                    {!existingReq && (
                      <Button size="sm" variant="outline" data-testid={`emp-correct-${rec.id}`} onClick={() => startEdit(rec)}>
                        Proponer corrección <ChevronRight className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="mt-3 pt-3 border-t space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs text-stone-600 block mb-1">Nueva entrada</label>
                        <Input type="datetime-local" value={form.clock_in}
                          onChange={(e) => setForm(f => ({ ...f, clock_in: e.target.value }))}
                          data-testid="form-clock-in" className="text-sm" />
                      </div>
                      <div>
                        <label className="text-xs text-stone-600 block mb-1">Nueva salida</label>
                        <Input type="datetime-local" value={form.clock_out}
                          onChange={(e) => setForm(f => ({ ...f, clock_out: e.target.value }))}
                          data-testid="form-clock-out" className="text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-stone-600 block mb-1">Motivo (obligatorio)</label>
                      <Textarea value={form.reason}
                        onChange={(e) => setForm(f => ({ ...f, reason: e.target.value }))}
                        placeholder="Ej: olvidé fichar la salida" rows={2}
                        data-testid="form-reason" className="text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={submit} data-testid="form-submit">
                        <Send className="w-3 h-3 mr-1" /> Enviar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={cancelEdit}>Cancelar</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EmployeeCorrectionsDialog;
